import { useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { Check, GripVertical, Pencil, Plus, Trash2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CH_STATUS, fmt } from "@/lib/writing";

export const StatusDot = ({ status, onChange, testId }) => {
  const cur = CH_STATUS.find((x) => x[0] === status) || CH_STATUS[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="p-1 shrink-0" title={`${cur[1]}: click to change`} data-testid={testId}><span className={`block w-2 h-2 rounded-full ${cur[2]}`} /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-panel border-line">
        {CH_STATUS.map(([k, l, dot]) => (
          <DropdownMenuItem key={k} onSelect={() => onChange(k)} data-testid={`${testId}-${k}`}><span className={`w-2 h-2 rounded-full mr-2 ${dot}`} />{l}</DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Row = ({ c, active, onPick, onRename, onDelete, onDrop, canDelete, onStatus }) => {
  const controls = useDragControls();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(c.title);
  const save = () => { setEditing(false); if (name.trim() && name.trim() !== c.title) onRename(c, name.trim()); else setName(c.title); };
  return (
    <Reorder.Item value={c.id} dragListener={false} dragControls={controls} onDragEnd={onDrop} data-testid={`chapter-item-${c.id}`}
      className={`group relative flex items-center gap-1 rounded-lg pr-1 transition-colors ${active ? "bg-amber-500/10" : "hover:bg-veil/[0.03]"}`}>
      {active && <span className="absolute left-0 top-2 bottom-2 w-0.5 rounded-full bg-amber-500" />}
      <button onPointerDown={(e) => controls.start(e)} className="touch-none cursor-grab active:cursor-grabbing p-1.5 text-slate-600 hover:text-slate-300" title="Drag to reorder" data-testid={`chapter-drag-${c.id}`}><GripVertical className="w-3.5 h-3.5" /></button>
      <StatusDot status={c.status} onChange={(st) => onStatus(c, st)} testId={`chapter-status-${c.id}`} />
      {editing ? (
        <form onSubmit={(e) => { e.preventDefault(); save(); }} className="flex flex-1 items-center gap-1 py-1">
          <input autoFocus value={name} maxLength={200} onChange={(e) => setName(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Escape" && (setName(c.title), setEditing(false))}
            className="flex-1 min-w-0 h-7 rounded bg-canvas border border-amber-500/40 px-2 text-sm text-slate-100 focus:outline-none" data-testid={`chapter-rename-input-${c.id}`} />
          <button className="p-1 text-emerald-300" data-testid={`chapter-rename-save-${c.id}`}><Check className="w-3.5 h-3.5" /></button>
        </form>
      ) : (
        <button onClick={() => onPick(c.id)} onDoubleClick={() => setEditing(true)} className="flex-1 min-w-0 text-left py-2" data-testid={`chapter-open-${c.id}`}>
          <span className={`block text-sm truncate ${active ? "text-amber-100" : "text-slate-300"}`}>{c.title}</span>
          <span className="block font-code text-[10px] text-slate-500" data-testid={`chapter-words-${c.id}`}>{fmt(c.words)} words</span>
        </button>
      )}
      {!editing && (
        <span className="flex opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={() => setEditing(true)} className="p-1 text-slate-500 hover:text-amber-300" title="Rename" data-testid={`chapter-rename-${c.id}`}><Pencil className="w-3.5 h-3.5" /></button>
          {canDelete && <button onClick={() => onDelete(c)} className="p-1 text-slate-500 hover:text-rose-300" title="Delete" data-testid={`chapter-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
        </span>
      )}
    </Reorder.Item>
  );
};

export const ChapterList = ({ chapters, activeId, onPick, onAdd, onRename, onDelete, onReorder, onDrop, onStatus }) => (
  <div className="space-y-3" data-testid="chapter-list">
    <p className="font-code text-[10px] uppercase tracking-[0.25em] text-slate-500 px-2">Chapters · {chapters.length}</p>
    <Reorder.Group axis="y" values={chapters.map((c) => c.id)} onReorder={onReorder} className="space-y-0.5">
      {chapters.map((c) => <Row key={c.id} c={c} active={c.id === activeId} onPick={onPick} onRename={onRename} onDelete={onDelete} onDrop={onDrop} onStatus={onStatus} canDelete={chapters.length > 1} />)}
    </Reorder.Group>
    <button onClick={onAdd} className="w-full flex items-center justify-center gap-1.5 h-9 rounded-lg border border-dashed border-line text-xs text-slate-400 hover:text-amber-300 hover:border-amber-500/40 transition-colors" data-testid="chapter-add-btn">
      <Plus className="w-3.5 h-3.5" /> Add chapter
    </button>
  </div>
);
